import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Tabs,
    Tab,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    IconButton,
    Chip
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Inventory as InventoryIcon,
    LocalShipping as SupplierIcon,
    SwapHoriz as TransactionIcon
} from '@mui/icons-material';
import { inventoryAPI } from '../services/api';
import { toast } from 'react-toastify';

const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`inventory-tabpanel-${index}`}
            aria-labelledby={`inventory-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
};

const Inventory = () => {
    const [tabValue, setTabValue] = useState(0);
    const [items, setItems] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogType, setDialogType] = useState(''); // 'item', 'supplier', 'transaction'
    const [formData, setFormData] = useState({});

    const fetchData = async () => {
        try {
            const [itemsRes, suppliersRes, transactionsRes] = await Promise.all([
                inventoryAPI.getItems(),
                inventoryAPI.getSuppliers(),
                inventoryAPI.getTransactions()
            ]);
            setItems(itemsRes.data.data);
            setSuppliers(suppliersRes.data.data);
            setTransactions(transactionsRes.data.data);
        } catch (error) {
            console.error('Error fetching inventory data:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleOpenDialog = (type, data = {}) => {
        setDialogType(type);
        setFormData(data);
        setOpenDialog(true);
    };

    const handleSubmit = async () => {
        try {
            if (formData.id) {
                if (dialogType === 'item') await inventoryAPI.updateItem(formData.id, formData);
                else if (dialogType === 'supplier') await inventoryAPI.updateSupplier(formData.id, formData);
                toast.success('Updated successfully');
            } else {
                if (dialogType === 'item') await inventoryAPI.createItem(formData);
                else if (dialogType === 'supplier') await inventoryAPI.createSupplier(formData);
                else if (dialogType === 'transaction') await inventoryAPI.createTransaction(formData);
                toast.success('Created successfully');
            }

            setOpenDialog(false);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Operation failed');
        }
    };

    const handleDelete = async (type, id) => {
        if (!window.confirm('Are you sure?')) return;
        try {
            if (type === 'item') await inventoryAPI.deleteItem(id);
            else if (type === 'supplier') await inventoryAPI.deleteSupplier(id);
            toast.success('Deleted successfully');
            fetchData();
        } catch (error) {
            toast.error('Delete failed');
        }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                Inventory Management
            </Typography>

            <Paper sx={{ width: '100%', mb: 2 }}>
                <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                    <Tab icon={<InventoryIcon />} label="Items" />
                    <Tab icon={<SupplierIcon />} label="Suppliers" />
                    <Tab icon={<TransactionIcon />} label="Transactions" />
                </Tabs>

                {/* Items Tab */}
                <TabPanel value={tabValue} index={0}>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenDialog('item')} sx={{ mb: 2 }}>
                        Add Item
                    </Button>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>SKU</TableCell>
                                    <TableCell>Quantity</TableCell>
                                    <TableCell>Unit</TableCell>
                                    <TableCell>Min Level</TableCell>
                                    <TableCell>Cost</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>{item.category}</TableCell>
                                        <TableCell>{item.sku}</TableCell>
                                        <TableCell>
                                            <Chip label={item.quantity} color={item.quantity <= item.minLevel ? 'error' : 'success'} />
                                        </TableCell>
                                        <TableCell>{item.unit}</TableCell>
                                        <TableCell>{item.minLevel}</TableCell>
                                        <TableCell>{item.unitCost}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => handleOpenDialog('item', item)}><EditIcon /></IconButton>
                                            <IconButton onClick={() => handleDelete('item', item.id)}><DeleteIcon /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Suppliers Tab */}
                <TabPanel value={tabValue} index={1}>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenDialog('supplier')} sx={{ mb: 2 }}>
                        Add Supplier
                    </Button>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Contact Person</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Phone</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {suppliers.map((supplier) => (
                                    <TableRow key={supplier.id}>
                                        <TableCell>{supplier.name}</TableCell>
                                        <TableCell>{supplier.contactPerson}</TableCell>
                                        <TableCell>{supplier.email}</TableCell>
                                        <TableCell>{supplier.phone}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => handleOpenDialog('supplier', supplier)}><EditIcon /></IconButton>
                                            <IconButton onClick={() => handleDelete('supplier', supplier.id)}><DeleteIcon /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                {/* Transactions Tab */}
                <TabPanel value={tabValue} index={2}>
                    <Button startIcon={<AddIcon />} variant="contained" onClick={() => handleOpenDialog('transaction')} sx={{ mb: 2 }}>
                        New Transaction
                    </Button>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Item</TableCell>
                                    <TableCell>Quantity</TableCell>
                                    <TableCell>Reference</TableCell>
                                    <TableCell>Performed By</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {transactions.map((txn) => (
                                    <TableRow key={txn.id}>
                                        <TableCell>{new Date(txn.createdAt).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            <Chip label={txn.type} color={txn.type === 'IN' ? 'primary' : 'warning'} />
                                        </TableCell>
                                        <TableCell>{txn.item?.name}</TableCell>
                                        <TableCell>{txn.quantity}</TableCell>
                                        <TableCell>{txn.reference}</TableCell>
                                        <TableCell>{txn.user?.username}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>
            </Paper>

            {/* Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {formData.id ? 'Edit' : 'Add'} {dialogType.charAt(0).toUpperCase() + dialogType.slice(1)}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>

                        {dialogType === 'item' && (
                            <>
                                <TextField label="Name" fullWidth value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                <TextField label="Category" fullWidth value={formData.category || ''} onChange={(e) => setFormData({ ...formData, category: e.target.value })} />
                                <TextField label="SKU" fullWidth value={formData.sku || ''} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} />
                                <TextField label="Unit (e.g., pcs, kg)" fullWidth value={formData.unit || ''} onChange={(e) => setFormData({ ...formData, unit: e.target.value })} />
                                <TextField label="Min Level" type="number" fullWidth value={formData.minLevel || 0} onChange={(e) => setFormData({ ...formData, minLevel: e.target.value })} />
                                <TextField label="Unit Cost" type="number" fullWidth value={formData.unitCost || 0} onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })} />
                                <TextField select label="Supplier" fullWidth value={formData.supplierId || ''} onChange={(e) => setFormData({ ...formData, supplierId: e.target.value })}>
                                    <MenuItem value="">None</MenuItem>
                                    {suppliers.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                                </TextField>
                            </>
                        )}

                        {dialogType === 'supplier' && (
                            <>
                                <TextField label="Name" fullWidth value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                                <TextField label="Contact Person" fullWidth value={formData.contactPerson || ''} onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })} />
                                <TextField label="Email" fullWidth value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                                <TextField label="Phone" fullWidth value={formData.phone || ''} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                                <TextField label="Address" fullWidth multiline rows={2} value={formData.address || ''} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                            </>
                        )}

                        {dialogType === 'transaction' && (
                            <>
                                <TextField select label="Type" fullWidth value={formData.type || 'IN'} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                                    <MenuItem value="IN">Stock In</MenuItem>
                                    <MenuItem value="OUT">Stock Out</MenuItem>
                                </TextField>
                                <TextField select label="Item" fullWidth value={formData.itemId || ''} onChange={(e) => setFormData({ ...formData, itemId: e.target.value })}>
                                    {items.map(i => <MenuItem key={i.id} value={i.id}>{i.name} (Stock: {i.quantity})</MenuItem>)}
                                </TextField>
                                <TextField label="Quantity" type="number" fullWidth value={formData.quantity || 1} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} />
                                <TextField label="Reference / Reason" fullWidth value={formData.reference || ''} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} />
                            </>
                        )}

                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained">Save</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Inventory;
